import { useEffect } from 'react';
import { sendToTerminal, logger } from '../../../lib/terminalLogger';
import {
  readPreparedPipelineId,
  readSnapshot,
  type PreparedSnapshotMeta,
} from '../../../lib/corel/pipeline/pipelineKeys';
import { convertToOrderObjects } from '../../../lib/corelOperations';

interface UseSnapshotHydrationParams {
  hydrateFromPreparedSnapshot?: boolean;
  pedidoId?: string;
  itemId?: string;
  expectedOpenSessionId?: string;
  selectedPedido: any;
  setSelectedObjects: React.Dispatch<React.SetStateAction<any[]>>;
  snapshotAppliedRef: React.MutableRefObject<boolean>;
  setSnapshotApplying: (applying: boolean) => void;
  isMonitorando: boolean;
  inicializarMonitoramento: () => Promise<void>;
  notificarWebViewDisponivel: () => Promise<void> | void;
  onSnapshotReady?: () => void;
}

function matchesPreparedContext(
  meta: PreparedSnapshotMeta | undefined,
  expectedOpenSessionId?: string
): boolean {
  if (!meta) return true;

  if (expectedOpenSessionId && meta.openSessionId && expectedOpenSessionId !== meta.openSessionId) {
    return false;
  }

  return true;
}

function getResolvedIds(
  pedidoId: string | undefined,
  itemId: string | undefined,
  selectedPedido: any
): { pid: string | number | undefined; iid: string | number | undefined } {
  const pid = (typeof pedidoId !== 'undefined' && pedidoId != null)
    ? pedidoId
    : selectedPedido?.orc_id;
  const iid = (typeof itemId !== 'undefined' && itemId != null)
    ? itemId
    : (selectedPedido as any)?.itens?.[0]?.oit_id;

  return { pid, iid };
}

export function useSnapshotHydration({
  hydrateFromPreparedSnapshot,
  pedidoId,
  itemId,
  expectedOpenSessionId,
  selectedPedido,
  setSelectedObjects,
  snapshotAppliedRef,
  setSnapshotApplying,
  isMonitorando,
  inicializarMonitoramento,
  notificarWebViewDisponivel,
  onSnapshotReady,
}: UseSnapshotHydrationParams) {
  useEffect(() => {
    if (!hydrateFromPreparedSnapshot) return;
    if (snapshotAppliedRef.current) return;

    const { pid, iid } = getResolvedIds(pedidoId, itemId, selectedPedido);
    if (!pid || !iid) return;

    const interval = setInterval(() => {
      if (snapshotAppliedRef.current) {
        clearInterval(interval);
        return;
      }

      const preparedPipelineId = readPreparedPipelineId(pid, iid);
      if (!preparedPipelineId) {
        return;
      }

      const envelope = readSnapshot(preparedPipelineId);
      const objetos = envelope?.objetos;
      const meta = envelope?.meta;
      const capturedAt = Number(meta?.capturedAt || 0);
      const withinTtl = capturedAt > 0 ? (Date.now() - capturedAt) <= 30000 : false;
      const contextMatches = matchesPreparedContext(meta, expectedOpenSessionId);

      if (!contextMatches) {
        sendToTerminal('⏭️ [SNAPSHOT:POLLING] SKIP - contexto divergente', {
          pipelineId: preparedPipelineId,
          expectedOpenSessionId,
          snapshotOpenSessionId: meta?.openSessionId,
        });
        return;
      }

      if (withinTtl && Array.isArray(objetos) && objetos.length > 0 && !snapshotAppliedRef.current) {
        snapshotAppliedRef.current = true;

        const mapped = convertToOrderObjects(objetos as any, 'mm') as any;
        sendToTerminal('✅ [SNAPSHOT:POLLING] Aplicando snapshot (fallback)', {
          mappedCount: mapped.length,
          pipelineId: preparedPipelineId,
          openSessionId: meta?.openSessionId,
        });

        setSelectedObjects(mapped as any);
        clearInterval(interval);

        if (onSnapshotReady) {
          onSnapshotReady();
        }

        setTimeout(() => setSnapshotApplying(false), 500);

        try {
          notificarWebViewDisponivel();
        } catch {}
      }
    }, 500);

    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 15000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [hydrateFromPreparedSnapshot, pedidoId, itemId, selectedPedido?.orc_id, expectedOpenSessionId, onSnapshotReady, setSelectedObjects, setSnapshotApplying, notificarWebViewDisponivel, snapshotAppliedRef]);

  useEffect(() => {
    const snapshotId = `snap-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    if (!hydrateFromPreparedSnapshot) {
      logger.debug(`⏭️ [${snapshotId}] SKIP - hydrateFromPreparedSnapshot is false`);
      sendToTerminal('⏭️ [SNAPSHOT] SKIP - hydrateFromPreparedSnapshot is false');
      return;
    }

    const monitorInitStartedRef = { current: false } as React.MutableRefObject<boolean>;
    void monitorInitStartedRef;
    void isMonitorando;
    void inicializarMonitoramento;

    (async () => {
      try {
        const { pid, iid } = getResolvedIds(pedidoId, itemId, selectedPedido);
        if (!pid || !iid) {
          return;
        }

        const preparedPipelineId = readPreparedPipelineId(pid, iid);

        if (snapshotAppliedRef.current) {
          return;
        }

        if (!preparedPipelineId) {
          sendToTerminal('⏭️ [SNAPSHOT] SKIP - preparedPipelineId não encontrado', { pid, iid });
          return;
        }

        setSnapshotApplying(true);
        const envelope = readSnapshot(preparedPipelineId);
        const objetos = envelope?.objetos;
        const meta = envelope?.meta;
        const capturedAt = Number(meta?.capturedAt || 0);
        const withinTtl = capturedAt > 0 ? (Date.now() - capturedAt) <= 30000 : false;
        const contextMatches = matchesPreparedContext(meta, expectedOpenSessionId);

        if (withinTtl && contextMatches && Array.isArray(objetos) && objetos.length > 0) {
          const mapped = convertToOrderObjects(objetos as any, 'mm') as any;

          sendToTerminal('✅ [SNAPSHOT] Aplicando snapshot', {
            objetosCount: mapped.length,
            pipelineId: preparedPipelineId,
            openSessionId: meta?.openSessionId,
          });

          setSelectedObjects(mapped as any);
          snapshotAppliedRef.current = true;

          if (onSnapshotReady) {
            onSnapshotReady();
          }
        } else {
          if (!contextMatches) {
            sendToTerminal('⏭️ [SNAPSHOT] SKIP - contexto divergente', {
              pipelineId: preparedPipelineId,
              expectedOpenSessionId,
              snapshotOpenSessionId: meta?.openSessionId,
            });
          }

          if (onSnapshotReady) {
            onSnapshotReady();
          }
        }

        setTimeout(() => setSnapshotApplying(false), 500);

        try {
          await notificarWebViewDisponivel();
        } catch {}
      } catch {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrateFromPreparedSnapshot, pedidoId, itemId, selectedPedido?.orc_id, expectedOpenSessionId]);
}
